/**
 * The Queue's right-hand preview pane — enrich-before-the-click: the
 * selected item's decision context (client, commercials, due, owner,
 * latest content, the case's activity so far) plus the triage verbs,
 * without leaving the queue. "Open" is the escape hatch into the full
 * case/report page.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge, Button, Card, Group, Menu, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconExternalLink } from '@tabler/icons-react';

import { EditableCodename } from '@/components/EditableCodename';
import { useAnalystRoster } from '@/hooks';
import { InboxAnalyst, InboxGroup, InboxItem } from '@/types/inbox';
import { currencySymbol } from '@/utils';
import { ActivityFeed } from './ActivityFeed';
import { deepLinkForInboxItem } from './deepLink';
import { dueInfo, kindChip, waitingNote } from './queueFormat';
import { AnalystAvatar, DueBadge } from './QueueRow';
import { timeAgo } from './timeAgo';

const CHIP_COLOR: Record<string, string> = {
  gray: 'gray',
  blue: 'blue',
  teal: 'teal',
  violet: 'violet',
  orange: 'orange',
  red: 'red',
};

interface QueuePreviewProps {
  item: InboxItem;
  /** The item's codename group (context: client, analyst, summary).
   * Null for snoozed rows served by the flat endpoint. */
  group: InboxGroup | null;
  currentUserId: string | null;
  onClaim: (familyKey: string, userId: string | null) => Promise<void>;
  /** Per-item assignee override (POST /inbox/{id}/assign). */
  onItemAssign: (item: InboxItem, userId: string | null) => Promise<void>;
  /** Fired after a codename rename (or a stale-key race) so the queue
   * regroups under the new key. */
  onCodenameRenamed?: () => void | Promise<void>;
  onRequestSnooze: (item: InboxItem) => void;
  onRequestDismiss: (item: InboxItem) => void;
  onRequestResolve: (item: InboxItem) => void;
}

function FactRow({ label, value }: Readonly<{ label: string; value: React.ReactNode }>) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="xs">
      <Text size="xs" c="dimmed" style={{ flex: 'none' }}>
        {label}
      </Text>
      <Text size="xs" ta="right" style={{ wordBreak: 'break-word' }} component="div">
        {value}
      </Text>
    </Group>
  );
}

function PaneCard({
  title,
  children,
  testId,
}: Readonly<{ title: string; children: React.ReactNode; testId?: string }>) {
  return (
    <Card shadow="sm" padding="md" radius="md" data-testid={testId}>
      <Stack gap="xs">
        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
          {title}
        </Text>
        {children}
      </Stack>
    </Card>
  );
}

export function QueuePreview({
  item,
  group,
  currentUserId,
  onClaim,
  onItemAssign,
  onCodenameRenamed,
  onRequestSnooze,
  onRequestDismiss,
  onRequestResolve,
}: Readonly<QueuePreviewProps>) {
  const navigate = useNavigate();
  const [claiming, setClaiming] = useState(false);
  const familyKey = item.codenameFamilyCached ?? group?.key ?? null;
  // Claim/assign candidates = the ANALYST roster (the current user's own
  // staff team), never the item's client team.
  const members = useAnalystRoster();
  const analyst: InboxAnalyst | null = group?.analyst ?? null;
  const claimable =
    familyKey !== null && group?.summary != null && group.summary.rootEntityType !== 'report';

  const chip = kindChip(item.kind);
  const link = deepLinkForInboxItem(item);
  const due = dueInfo(item);
  const waiting = waitingNote(item);
  const requesterEmail =
    typeof item.extra?.requester_email === 'string' ? item.extra.requester_email : null;

  const claimAs = async (userId: string | null) => {
    if (!familyKey) {
      return;
    }
    setClaiming(true);
    try {
      await onClaim(familyKey, userId);
    } finally {
      setClaiming(false);
    }
  };

  const currency = currencySymbol(item.currency);

  return (
    <Stack gap="sm" data-testid="queue-preview">
      <Group gap={8}>
        <Badge
          size="sm"
          variant="light"
          color={CHIP_COLOR[chip.tone]}
          style={{ textTransform: 'none' }}
        >
          {chip.label}
        </Badge>
        {item.expedited && (
          <Badge size="sm" variant="light" color="orange">
            Expedited
          </Badge>
        )}
        <Text size="xs" c="dimmed">
          {timeAgo(item.updatedAt ?? item.createdAt)}
        </Text>
      </Group>

      <Group gap={8} align="baseline" wrap="wrap">
        {familyKey ? (
          <EditableCodename
            value={familyKey}
            size="md"
            fw={600}
            onSaved={onCodenameRenamed}
            onStaleKey={onCodenameRenamed}
            data-testid="preview-codename"
          />
        ) : (
          <Text fw={600} size="lg" data-testid="preview-codename">
            {item.codenameCached ?? '—'}
          </Text>
        )}
        {group?.client && (
          <Text size="sm" c="dimmed" fw={500}>
            {group.client.name}
          </Text>
        )}
        {group?.client?.clientCode && (
          <Badge size="xs" variant="outline" color="gray">
            {group.client.clientCode}
          </Badge>
        )}
      </Group>
      <Text size="sm" c="dimmed" data-testid="preview-title">
        {item.title}
      </Text>

      <Group gap={8} wrap="wrap">
        {claimable &&
          (analyst ? (
            <Menu withinPortal position="bottom-start">
              <Menu.Target>
                <Button
                  variant="default"
                  size="xs"
                  loading={claiming}
                  leftSection={<AnalystAvatar analyst={analyst} size={18} />}
                  rightSection={<IconChevronDown size={12} />}
                  data-testid="preview-owner-menu"
                >
                  {analyst.name ?? analyst.userId}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {currentUserId && analyst.userId !== currentUserId && (
                  <Menu.Item onClick={() => void claimAs(currentUserId)}>Claim for me</Menu.Item>
                )}
                {members
                  .filter((m) => String(m.id) !== analyst.userId)
                  .map((m) => (
                    <Menu.Item key={m.id} onClick={() => void claimAs(String(m.id))}>
                      Reassign to {m.name}
                    </Menu.Item>
                  ))}
                <Menu.Divider />
                <Menu.Item color="red" onClick={() => void claimAs(null)}>
                  Release case
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          ) : (
            <Button.Group>
              <Button
                size="xs"
                loading={claiming}
                disabled={!currentUserId}
                onClick={() => void claimAs(currentUserId)}
                data-testid="preview-claim"
              >
                Claim case
              </Button>
              <Menu withinPortal position="bottom-start">
                <Menu.Target>
                  <Button
                    size="xs"
                    px={6}
                    aria-label="Assign case to…"
                    data-testid="preview-assign-menu"
                  >
                    <IconChevronDown size={12} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Assign case to…</Menu.Label>
                  {members.length === 0 && <Menu.Item disabled>No team members</Menu.Item>}
                  {members.map((m) => (
                    <Menu.Item key={m.id} onClick={() => void claimAs(String(m.id))}>
                      {m.name}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Button.Group>
          ))}
        {item.state !== 'snoozed' && (
          <Button variant="default" size="xs" onClick={() => onRequestSnooze(item)}>
            Snooze
          </Button>
        )}
        <Button variant="default" size="xs" onClick={() => onRequestResolve(item)}>
          Resolve
        </Button>
        <Button variant="subtle" color="red" size="xs" onClick={() => onRequestDismiss(item)}>
          Dismiss
        </Button>
        {link && (
          <Button
            size="xs"
            variant="filled"
            color="navy"
            ml="auto"
            rightSection={<IconExternalLink size={12} />}
            onClick={() => navigate(link.path)}
            data-testid="preview-open"
          >
            {link.label}
          </Button>
        )}
      </Group>

      <PaneCard title="Case facts" testId="preview-facts">
        {group?.client && <FactRow label="Client" value={group.client.name} />}
        {item.price != null && (
          <FactRow
            label="Price / TAT"
            value={`${currency}${Number(item.price).toLocaleString('en-GB')}${
              item.turnaroundDays != null ? ` · ${item.turnaroundDays} days` : ''
            }`}
          />
        )}
        {due && (
          <FactRow
            label="Due"
            value={
              <Group gap={6} justify="flex-end">
                <DueBadge item={item} />
                <Text size="xs" component="span">
                  {due.exact}
                </Text>
              </Group>
            }
          />
        )}
        {waiting && <FactRow label="Status" value={waiting} />}
        {requesterEmail && <FactRow label="Requester" value={requesterEmail} />}
        <FactRow
          label="Case analyst"
          value={analyst ? (analyst.name ?? analyst.userId) : 'Unclaimed'}
        />
        <FactRow
          label="This item"
          value={
            <Menu withinPortal position="bottom-end">
              <Menu.Target>
                <UnstyledButton data-testid="preview-item-assignee">
                  <Group gap={4} wrap="nowrap" justify="flex-end">
                    <Text size="xs" c={item.assignee ? undefined : 'dimmed'} fw={500}>
                      {item.assignee
                        ? (item.assignee.name ?? item.assignee.userId)
                        : 'Follows case analyst'}
                    </Text>
                    <IconChevronDown size={11} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Hand this item to…</Menu.Label>
                {currentUserId && item.assignee?.userId !== currentUserId && (
                  <Menu.Item onClick={() => void onItemAssign(item, currentUserId)}>Me</Menu.Item>
                )}
                {members
                  .filter((m) => String(m.id) !== item.assignee?.userId)
                  .map((m) => (
                    <Menu.Item key={m.id} onClick={() => void onItemAssign(item, String(m.id))}>
                      {m.name}
                    </Menu.Item>
                  ))}
                {item.assignee && (
                  <>
                    <Menu.Divider />
                    <Menu.Item color="red" onClick={() => void onItemAssign(item, null)}>
                      Clear override
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          }
        />
        {group?.summary && group.summary.subjectNames.length > 0 && (
          <FactRow
            label={`Subjects (${group.summary.subjectTotal})`}
            value={group.summary.subjectNames.join(', ')}
          />
        )}
        {item.state === 'snoozed' && item.snoozeReason && (
          <FactRow label="Snooze reason" value={item.snoozeReason} />
        )}
      </PaneCard>

      {item.body && (
        <PaneCard title="Latest" testId="preview-body">
          <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
            {item.body}
          </Text>
        </PaneCard>
      )}

      {familyKey && (
        <PaneCard title="Activity" testId="preview-activity">
          <ActivityFeed familyKey={familyKey} />
        </PaneCard>
      )}
    </Stack>
  );
}
